// razorpay.ts — shared Razorpay Checkout loader for the web app. The backend
// creates the order and returns { orderId, keyId, amount, currency }; the pages
// open Checkout against it and POST the signed handshake to /payments/callback.

/** The Checkout handshake handed to the success handler. */
export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export interface RazorpayCheckoutOptions {
  key: string;
  order_id: string;
  amount: number;
  currency: string;
  name?: string;
  description?: string;
  image?: string;
  handler?: (response: RazorpayResponse) => void;
  modal?: { ondismiss?: () => void };
  theme?: { color?: string };
  prefill?: { email?: string; name?: string };
}

interface RazorpayInstance { open(): void }
type RazorpayCtor = new (options: RazorpayCheckoutOptions) => RazorpayInstance;
declare global { interface Window { Razorpay?: RazorpayCtor } }

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
/** BREW accent — used as the Checkout theme colour. */
export const RAZORPAY_THEME_COLOR = '#473C38';

let checkoutPromise: Promise<RazorpayCtor> | null = null;

/** Lazily inject the Razorpay Checkout script once and resolve its constructor. */
export function loadRazorpay(): Promise<RazorpayCtor> {
  if (window.Razorpay) return Promise.resolve(window.Razorpay);
  if (checkoutPromise) return checkoutPromise;
  checkoutPromise = new Promise<RazorpayCtor>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = CHECKOUT_SRC;
    s.async = true;
    s.onload = () => (window.Razorpay ? resolve(window.Razorpay) : reject(new Error('Razorpay failed to initialise.')));
    s.onerror = () => { checkoutPromise = null; reject(new Error('Could not load Razorpay Checkout — check your connection and try again.')); };
    document.body.appendChild(s);
  });
  return checkoutPromise;
}

/** Load Checkout and open a modal for `options`. */
export async function openRazorpayCheckout(options: RazorpayCheckoutOptions): Promise<void> {
  const Razorpay = await loadRazorpay();
  new Razorpay(options).open();
}

/** Dynamically resolve the absolute URL of the brand logo for Checkout.
 *  Points to `landing/public/brew-logo.png` which is served at the stable
 *  path `/brew-logo.png` — unlike `app/icon.png` which is a Next.js favicon
 *  convention file and does not have a predictable public URL.
 */
export function getCheckoutImage(): string {
  const { protocol, hostname, port } = window.location;
  if (port) {
    // In development environments with a port, the landing page is hosted on port 4010
    return `${protocol}//${hostname}:4010/brew-logo.png`;
  }
  // In production (no explicit port), the landing page is at the apex domain (without 'assist.')
  return `${protocol}//${hostname.replace('assist.', '')}/brew-logo.png`;
}
